import React from "react";
import { useOutletContext } from "react-router-dom";
import { USE_LOCAL_FALLBACK } from "../../config/firebaseConfig";
import { books_getInventory, books_getBundle, books_saveBundle } from "../../lib/firestoreAdapters";

function keyInv(campus, sy) { return `books_inventory_${String(campus).toLowerCase()}_${sy}`; }
function loadInv(campus, sy) { try { const raw = localStorage.getItem(keyInv(campus, sy)); return raw ? JSON.parse(raw) : []; } catch { return []; } }
function keyBundles(campus, sy) { return `books_bundles_${String(campus).toLowerCase()}_${sy}`; }
function loadBundles(campus, sy) { try { const raw = localStorage.getItem(keyBundles(campus, sy)); return raw ? JSON.parse(raw) : []; } catch { return []; } }
function saveBundles(campus, sy, data) { try { localStorage.setItem(keyBundles(campus, sy), JSON.stringify(data)); } catch (e) { void 0; } }

const GRADES = ["Nursery","Kinder","Prep","Grade 1","Grade 2","Grade 3","Grade 4","Grade 5","Grade 6","Grade 7","Grade 8","Grade 9","Grade 10","Grade 11","Grade 12"];

export default function Bundles() {
  const { campus, schoolYear } = useOutletContext();
  const [books, setBooks] = React.useState(() => loadInv(campus, schoolYear));
  const [bundles, setBundles] = React.useState(() => loadBundles(campus, schoolYear));
  const [grade, setGrade] = React.useState(GRADES[0]);
  const [selected, setSelected] = React.useState([]);
  const [overridePrice, setOverridePrice] = React.useState("");
  React.useEffect(() => {
    async function fetch() {
      if (USE_LOCAL_FALLBACK) {
        setBooks(loadInv(campus, schoolYear));
        setBundles(loadBundles(campus, schoolYear));
      } else {
        const inv = await books_getInventory(campus, schoolYear);
        setBooks(inv);
        // We fetch on demand per grade below
      }
      setSelected([]); setOverridePrice("");
    }
    fetch();
  }, [campus, schoolYear]);

  const gradeBooks = React.useMemo(() => books.filter(b => (b.grade || "").toLowerCase() === (grade || "").toLowerCase()), [books, grade]);
  const total = React.useMemo(() => {
    const sum = selected.reduce((s, id) => {
      const b = gradeBooks.find(x => x.id === id);
      return s + Number(b?.price || 0);
    }, 0);
    return sum;
  }, [selected, gradeBooks]);

  function toggle(id) {
    setSelected(sel => sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]);
  }
  async function save() {
    const items = selected.map(id => {
      const b = gradeBooks.find(x => x.id === id);
      return { id: b.id, title: b.title, price: Number(b.price || 0) };
    });
    const rec = {
      id: `G-${grade}-${schoolYear}`,
      grade,
      schoolYear,
      items,
      price: overridePrice ? Number(overridePrice) : items.reduce((s, it) => s + Number(it.price || 0), 0)
    };
    if (USE_LOCAL_FALLBACK) {
      const next = bundles.filter(b => !(b.grade === grade && b.schoolYear === schoolYear)).concat(rec);
      setBundles(next); saveBundles(campus, schoolYear, next);
      alert("Bundle saved");
    } else {
      await books_saveBundle(campus, schoolYear, grade, items, rec.price);
      alert("Bundle saved (Firestore)");
    }
  }

  React.useEffect(() => {
    async function fetchBundle() {
      if (!USE_LOCAL_FALLBACK) {
        const b = await books_getBundle(campus, schoolYear, grade);
        if (b) {
          setSelected(b.items.map(it => it.id));
          setOverridePrice(String(b.price || ""));
        } else {
          setSelected([]); setOverridePrice("");
        }
      }
    }
    fetchBundle();
  }, [campus, schoolYear, grade]);

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <h5 className="mb-0">Bundles — {campus.toUpperCase()} — {schoolYear}</h5>
          <select className="form-select" value={grade} onChange={e => setGrade(e.target.value)}>
            {GRADES.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <div className="d-flex align-items-center gap-2">
          <input className="form-control" placeholder="Override bundle price (optional)" value={overridePrice} onChange={e => setOverridePrice(e.target.value)} style={{ width: 260 }} />
          <button className="btn btn-primary" onClick={save} disabled={selected.length === 0}>Save Bundle</button>
        </div>
      </div>
      <div className="row g-3">
        <div className="col-md-7">
          <div className="card">
            <div className="card-body">
              <h6 className="fw-bold">Select Books for {grade}</h6>
              <div className="table-responsive" style={{ maxHeight: 420, overflowY: "auto" }}>
                <table className="table table-sm table-hover">
                  <thead><tr><th></th><th>Title</th><th className="text-end">Price</th></tr></thead>
                  <tbody>
                    {gradeBooks.length === 0 ? (
                      <tr><td colSpan="3" className="text-center text-muted">No books tagged for {grade}</td></tr>
                    ) : gradeBooks.map(b => (
                      <tr key={b.id}>
                        <td>
                          <input type="checkbox" checked={selected.includes(b.id)} onChange={() => toggle(b.id)} />
                        </td>
                        <td>{b.title}</td>
                        <td className="text-end">₱{Number(b.price || 0).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-5">
          <div className="card">
            <div className="card-body">
              <h6 className="fw-bold">Bundle Summary</h6>
              <ul className="mb-2">
                {selected.map(id => {
                  const b = gradeBooks.find(x => x.id === id);
                  return <li key={id}>{b?.title} — ₱{Number(b?.price || 0).toLocaleString()}</li>;
                })}
              </ul>
              <div className="d-flex justify-content-between border-top pt-2">
                <strong>Total (auto)</strong>
                <strong>₱{total.toLocaleString()}</strong>
              </div>
              {overridePrice && (
                <div className="d-flex justify-content-between">
                  <strong>Override</strong>
                  <strong>₱{Number(overridePrice || 0).toLocaleString()}</strong>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
